/**
 * AUTH SUPPORT - DO NOT MODIFY WITHOUT READING
 *
 * getApiHeaders() builds headers based on auth provider:
 * - Basic auth / passcode: Sends the stored session token as Authorization: Bearer
 * - Okta: Sends Authorization: Bearer token (backend validates JWT, extracts claims)
 *
 * Claims are extracted server-side from the signed token, not from spoofable headers.
 * See frontend/CLAUDE.md "Authentication" section for details.
 */
import { AuthState } from '../hooks/useAuth';
import { BASIC_AUTH_TOKEN_KEY } from '../components/BasicLoginScreen';

const API_BASE = '/api/v1';

// Global auth store - set by ApiContext, used automatically by all API calls
let _globalAuth: AuthState | null = null;

// Callback for 401 handling (set by ApiContext to trigger re-login)
let _on401Callback: (() => void) | null = null;

/**
 * Set the global auth context. Called by ApiContext when auth changes.
 * This allows API functions to automatically use auth without explicit passing.
 */
export function setGlobalAuth(auth: AuthState | null): void {
  _globalAuth = auth;
}

/**
 * Get the current global auth context.
 */
export function getGlobalAuth(): AuthState | null {
  return _globalAuth;
}

/**
 * Register callback to handle 401 responses (triggers re-login).
 * Called by ApiContext to avoid circular imports.
 */
export function setOn401Callback(callback: (() => void) | null): void {
  _on401Callback = callback;
}

/**
 * API error with status code and parsed response body.
 * Thrown by request<T>() when the response is not ok.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string
  ) {
    super(message || `API error ${status}`);
    this.name = 'ApiError';
  }

  /** Extract the 'detail' field from FastAPI error responses */
  get detail(): string | undefined {
    if (typeof this.body === 'object' && this.body !== null && 'detail' in this.body) {
      return String((this.body as { detail: unknown }).detail);
    }
    return undefined;
  }
}

/**
 * Build headers for API requests.
 * - Basic auth / passcode: Bearer session token
 * - Okta: Uses Bearer token + X-ID-Token header (groups are in ID token, not access token)
 * - Fallback: API key for local dev
 */
async function getApiHeaders(auth?: AuthState | null): Promise<HeadersInit> {
  // Use explicit auth if provided, otherwise use global auth
  const effectiveAuth = auth !== undefined ? auth : _globalAuth;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Basic auth mode (pilot tenants): send the stored basic-auth session token.
  const basicAuthToken = localStorage.getItem(BASIC_AUTH_TOKEN_KEY);
  if (basicAuthToken) {
    headers['Authorization'] = `Bearer ${basicAuthToken}`;
    return headers;
  }

  // Passcode mode (lensdemo): send the stored passcode session token.
  // Takes precedence over Okta/API-key — these modes are mutually exclusive.
  const passcodeToken = localStorage.getItem('lens_passcode_token');
  if (passcodeToken) {
    headers['Authorization'] = `Bearer ${passcodeToken}`;
    return headers;
  }

  if (effectiveAuth?.isAuthenticated && effectiveAuth.getAccessToken) {
    // Okta: Send Bearer token
    // Backend validates JWT and extracts claims (sub, email, org_id, etc.)
    const token = await effectiveAuth.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;

      // Send ID token for IDP group extraction (groups are in ID token, not access token)
      if (effectiveAuth.getIdToken) {
        const idToken = await effectiveAuth.getIdToken();
        if (idToken) {
          headers['X-ID-Token'] = idToken;
        }
      }

      return headers;
    } else {
      console.warn('[API] getAccessToken returned null/undefined');
    }
  }

  // Fallback to API key (local dev without auth)
  const apiKey = (window as { __RUNTIME_CONFIG__?: { LENS_API_KEY?: string } }).__RUNTIME_CONFIG__?.LENS_API_KEY;
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }

  return headers;
}

/**
 * Fetch wrapper that includes authentication headers.
 * IMPORTANT: Use this instead of raw fetch() for all API calls.
 */
export async function apiFetch(
  url: string,
  options?: RequestInit,
  auth?: AuthState | null
): Promise<Response> {
  const headers = await getApiHeaders(auth);

  // Don't set Content-Type for FormData - browser sets it with boundary
  const isFormData = options?.body instanceof FormData;
  if (isFormData) {
    delete (headers as Record<string, string>)['Content-Type'];
  }

  return fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...(options?.headers || {}),
    },
  });
}

/**
 * Typed request helper with auth injection, error parsing, and 401 handling.
 *
 * @param path - API path relative to /api/v1 (e.g., '/health/summary')
 * @param init - Optional RequestInit (method, body, headers)
 * @returns Parsed JSON response of type T
 * @throws ApiError with status and body on non-ok responses
 *
 * On 401: triggers the registered re-login callback (if set) and throws.
 */
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await getApiHeaders();

  // Don't set Content-Type for FormData - browser sets it with boundary
  const isFormData = init?.body instanceof FormData;
  const finalHeaders: Record<string, string> = { ...(headers as Record<string, string>) };
  if (isFormData) {
    delete finalHeaders['Content-Type'];
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...finalHeaders,
      ...(init?.headers || {}),
    },
  });

  if (!response.ok) {
    // Parse error body (FastAPI returns { detail: string } for most errors)
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // Body may not be JSON
    }

    // Handle 401 - trigger re-login
    if (response.status === 401) {
      // Passcode mode: clear the stale/expired token so the app returns to the passcode screen
      localStorage.removeItem('lens_passcode_token');
      if (_on401Callback) {
        _on401Callback();
      }
    }

    // Extract detail message for error
    let message = `API error ${response.status}`;
    if (typeof body === 'object' && body !== null && 'detail' in body) {
      message = String((body as { detail: unknown }).detail);
    }

    throw new ApiError(response.status, body, message);
  }

  return response.json();
}

/**
 * Typed fetch helper for endpoints that return binary/blob data.
 * Handles auth injection and 401 redirect like request<T>.
 */
export async function requestBlob(path: string, init?: RequestInit): Promise<Blob> {
  const headers = await getApiHeaders();
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(headers as Record<string, string>),
      ...(init?.headers || {}),
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      // Passcode mode: clear the stale/expired token so the app returns to the passcode screen
      localStorage.removeItem('lens_passcode_token');
      if (_on401Callback) {
        _on401Callback();
      }
    }
    throw new ApiError(response.status, null, `API error ${response.status}`);
  }

  return response.blob();
}

export interface DeviceHealth {
  uuid: string;
  hostname?: string;
  status: 'healthy' | 'warning' | 'critical' | 'unknown';
  issues: HealthIssue[];
  issue_count: number;
  metrics: Record<string, number | string>;
  engine_score: number | null;
  engine_status: string | null;
  composite_score: number | null;
  composite_status: 'excellent' | 'good' | 'fair' | 'poor' | 'critical' | null;
  composite_components: {
    cpu: number | null;
    memory: number | null;
    storage: number | null;
    battery: number | null;
    stability: number | null;
    age_modifier: number | null;
  };
  composite_inputs: {
    cpu: number | null;
    memory: number | null;
    storage: number | null;
    battery: number | null;
    stability: number | null;
  };
  evaluated_at: string;
}

export interface HealthIssue {
  metric: string;
  label: string;
  value: number;
  unit: string;
  status: string;
  message: string;
}

export interface FleetSummary {
  total_devices: number;
  health: {
    total_devices: number;
    healthy: number;
    warning: number;
    critical: number;
    unknown: number;
    healthy_percent: number;
    warning_percent: number;
    critical_percent: number;
    computed_at: string;
  };
}

export interface Device {
  uuid: string;
  hostname: string;
  serial_number?: string;
  hardware_model?: string;
  os_version?: string;
  os_build?: string;
  arch?: string;
  processor?: string;
  cpu_cores?: number;
  cpu_p_cores?: number;
  cpu_e_cores?: number;
  memory_gb?: number;
  storage_gb?: number;
  app_version?: string;
  osquery_version?: string;
  user_name?: string;
  user_email?: string;
  department?: string;
  jamf_id?: number | null;
  extra_fields?: Record<string, string>;
}

export interface AtRiskDevice {
  uuid: string;
  hostname: string;
  status: string;
  score: number | null;
  engine_status: string | null;
  issues: HealthIssue[];
  issue_count: number;
  composite_score: number | null;
  composite_status: string | null;
  evaluated_at: string;
}

export interface InventoryResponse {
  devices: Device[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface AtRiskResponse {
  devices: AtRiskDevice[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface TCOSummary {
  total_devices: number;
  total_acquisition_cost: number;
  total_current_value: number;
  total_annual_tco: number;
  five_year_tco: number;
  cost_per_device_per_year: number;
  average_device_age: number;
  devices_past_lifecycle: number;
}

export interface LifecycleDistribution {
  bracket: string;
  count: number;
  percentage: number;
}

export interface RefreshForecast {
  fiscal_year: string;
  device_count: number;
  replacement_cost: number;
  trade_in_value: number;
  net_cost: number;
}

export interface UtilizationMetrics {
  total_devices: number;
  active_devices: number;
  inactive_devices: number;
  utilization_rate: number;
  underutilized_count: number;
  potential_savings: number;
}

export interface DepartmentTCO {
  department: string;
  device_count: number;
  total_current_value: number;
  total_annual_tco: number;
  avg_device_age: number;
  devices_needing_refresh: number;
  refresh_budget: number;
}

export interface ModelBreakdown {
  model_category: string;
  device_count: number;
  total_msrp: number;
  total_current_value: number;
  avg_age_years: number;
}

export interface DeviceTCODetail {
  device_id: string;
  uuid: string;
  device_name: string;
  user_name: string;
  model: string;
  department: string;
  age_years: number;
  health_score: number;
  msrp: number;
  current_value: number;
  annual_tco: number;
  years_remaining: number;
  needs_refresh: boolean;
  priority: string;
}

export interface TCODashboard {
  summary: TCOSummary;
  lifecycle_distribution: LifecycleDistribution[];
  refresh_forecast: RefreshForecast[];
  utilization: UtilizationMetrics;
  by_department: DepartmentTCO[];
  model_breakdown: ModelBreakdown[];
  top_replacement_candidates: DeviceTCODetail[];
}

// Comprehensive TCO with cost breakdown
export interface ComprehensiveTCOSummary {
  total_devices: number;
  lifecycle_years: number;
  total_tco: number;
  annual_tco: number;
  cost_per_device: number;
  hardware_costs: number;
  software_costs: number;
  support_costs: number;
  energy_costs: number;
  hardware_pct: number;
  software_pct: number;
  support_pct: number;
  energy_pct: number;
}

// ROI Types
export interface CostBreakdown {
  hardware_net: number;
  software: number;
  support: number;
  energy: number;
}

export interface ValueBreakdown {
  productivity: number;
  uptime: number;
  security: number;
  retention: number;
}

export interface ROISummary {
  device_count: number;
  lifecycle_years: number;
  total_tco: number;
  total_value_generated: number;
  net_value: number;
  roi_percentage: number;
  payback_years: number;
  avg_annual_tco: number;
  avg_annual_value: number;
  avg_annual_net: number;
}

export interface SoftwareCostConfig {
  mdm: number;
  mdm_support: number;
  security_suite: number;
  vulnerability_mgmt: number;
  backup: number;
  password_manager: number;
  authentication: number;
  vpn: number;
  productivity: number;
  collaboration: number;
  total_annual: number;
}

export interface SupportCostConfig {
  help_desk: number;
  asset_management: number;
  platform_engineering: number;
  security_engineering: number;
  external_support: number;
  total_annual: number;
}

export interface ROIFactorConfig {
  avg_employee_salary: number;
  productivity_hours_saved_per_year: number;
  expected_downtime_hours_per_year: number;
  incident_probability_reduction: number;
  avg_incident_cost: number;
  turnover_reduction_pct: number;
  avg_replacement_cost: number;
  total_annual_value: number;
}

export interface CostConfiguration {
  software_costs: SoftwareCostConfig;
  support_costs: SupportCostConfig;
  roi_factors: ROIFactorConfig;
}

export interface DepartmentROI {
  department: string;
  device_count: number;
  total_tco: number;
  total_value: number;
  net_value: number;
  roi_percentage: number;
}

export interface ModelROI {
  model_category: string;
  device_count: number;
  total_tco: number;
  total_value: number;
  net_value: number;
  roi_percentage: number;
}

export interface ROIDashboard {
  summary: ROISummary;
  tco_breakdown: ComprehensiveTCOSummary;
  value_breakdown: ValueBreakdown;
  cost_configuration: CostConfiguration;
  top_roi_devices: DeviceTCODetail[];
  by_department: DepartmentROI[];
  by_model_category: ModelROI[];
}

export interface FleetDeviceHealth {
  uuid: string;
  hostname: string;
  serial_number: string | null;
  status: string;
  composite_score: number | null;
  composite_status: 'excellent' | 'good' | 'fair' | 'poor' | 'critical' | null;
  composite_components: {
    cpu: number | null;
    memory: number | null;
    storage: number | null;
    battery: number | null;
    stability: number | null;
    age_modifier: number | null;
  } | null;
  issues: HealthIssue[];
  issue_count: number;
  evaluated_at: string;
  jamf_id: number | null;
  hardware_model: string | null;
  assigned_user: string | null;
  department: string | null;
  app_version: string | null;
  device_age_years: number | null;
  warranty_status: 'active' | 'expiring_soon' | 'expired' | 'unknown' | null;
  last_seen: string | null;
}

// API Functions
// NOTE: Functions below use the new request<T> helper which handles:
// - Auth header injection (from global auth context)
// - Error body parsing (extracts FastAPI 'detail' field)
// - 401 handling (triggers re-login callback)
// The `auth` parameter is kept for backward compatibility but is ignored when
// using request<T>() - global auth is used automatically.

export async function fetchFleetSummary(_auth?: AuthState | null): Promise<FleetSummary> {
  return request<FleetSummary>('/health/summary');
}

export async function fetchFleetDeviceHealth(
  page = 1,
  pageSize = 100,
  status?: 'healthy' | 'warning' | 'critical' | 'at-risk',
  sortBy = 'hostname',
  sortDesc = false,
  _auth?: AuthState | null
): Promise<{ devices: FleetDeviceHealth[]; total: number; total_pages: number }> {
  let path = `/health/fleet/devices?page=${page}&page_size=${pageSize}&sort_by=${sortBy}&sort_desc=${sortDesc}`;
  if (status) path += `&status=${status}`;
  return request<{ devices: FleetDeviceHealth[]; total: number; total_pages: number }>(path);
}

export async function fetchDeviceHealth(uuid: string, forceCompute = false, _auth?: AuthState | null): Promise<DeviceHealth> {
  const path = forceCompute
    ? `/health/${uuid}?force_compute=true`
    : `/health/${uuid}`;
  return request<DeviceHealth>(path);
}

/**
 * Force recalculate device health from ClickHouse AND update Redis state.
 * Use this when health score needs to be refreshed and persisted.
 */
export async function refreshDeviceHealth(uuid: string, _auth?: AuthState | null): Promise<DeviceHealth> {
  return request<DeviceHealth>(`/health/${uuid}?refresh=true`);
}

export async function fetchBulkDeviceHealth(uuids: string[], _auth?: AuthState | null): Promise<Record<string, DeviceHealth>> {
  const data = await request<{ devices: DeviceHealth[] }>('/health/bulk', {
    method: 'POST',
    body: JSON.stringify(uuids),
  });
  // Convert array to map keyed by uuid
  const result: Record<string, DeviceHealth> = {};
  for (const device of data.devices) {
    result[device.uuid] = device;
  }
  return result;
}

export async function fetchAtRiskDevices(page = 1, pageSize = 25, _auth?: AuthState | null): Promise<AtRiskResponse> {
  return request<AtRiskResponse>(`/health/at-risk?page=${page}&page_size=${pageSize}`);
}

export async function fetchInventory(
  page = 1,
  pageSize = 25,
  sortBy = 'hostname',
  sortDesc = false,
  search?: string,
  _auth?: AuthState | null,
  uuids?: string[]
): Promise<InventoryResponse> {
  let path = `/inventory?page=${page}&page_size=${pageSize}&sort_by=${sortBy}&sort_desc=${sortDesc}`;
  if (search) {
    // Use search parameter to search across hostname, UUID, serial, user, and department
    path += `&search=${encodeURIComponent(search)}`;
  }
  if (uuids && uuids.length > 0) {
    // Use RSQL OR query to filter by specific UUIDs (for performance filter)
    const query = uuids.map(u => `uuid==${u}`).join(',');
    path += `&query=${encodeURIComponent(query)}`;
  }
  return request<InventoryResponse>(path);
}

export async function fetchDeviceInventory(uuid: string, _auth?: AuthState | null): Promise<Device | null> {
  const data = await request<InventoryResponse>(`/inventory?query=uuid==${encodeURIComponent(uuid)}&page_size=1`);
  return data.devices.length > 0 ? data.devices[0] : null;
}

export async function fetchBulkInventory(uuids: string[], _auth?: AuthState | null): Promise<Record<string, Device>> {
  if (uuids.length === 0) return {};
  // Use RSQL OR query: uuid==id1,uuid==id2,uuid==id3
  const query = uuids.map(u => `uuid==${u}`).join(',');
  const data = await request<InventoryResponse>(`/inventory?query=${encodeURIComponent(query)}&page_size=${uuids.length}`);
  const result: Record<string, Device> = {};
  for (const device of data.devices) {
    result[device.uuid] = device;
  }
  return result;
}

export interface DepartmentsResponse {
  departments: string[];
  count: number;
}

export async function fetchDepartments(_auth?: AuthState | null): Promise<string[]> {
  const data = await request<DepartmentsResponse>('/inventory/departments');
  return data.departments;
}

export interface ScoreDistributionBucket {
  key: 'excellent' | 'good' | 'fair' | 'poor' | 'critical';
  label: string;
  count: number;
}

export interface ScoreDistribution {
  buckets: ScoreDistributionBucket[];
  unscored: number;
  total: number;
}

export async function fetchScoreDistribution(_auth?: AuthState | null): Promise<ScoreDistribution> {
  return request<ScoreDistribution>('/health/fleet/score-distribution');
}

export async function fetchTCOSummary(_auth?: AuthState | null): Promise<TCOSummary> {
  return request<TCOSummary>('/tco/summary');
}

export async function fetchTCODashboard(_auth?: AuthState | null): Promise<TCODashboard> {
  return request<TCODashboard>('/tco/dashboard');
}

export async function fetchComprehensiveTCO(lifecycleYears = 5, _auth?: AuthState | null): Promise<ComprehensiveTCOSummary> {
  return request<ComprehensiveTCOSummary>(`/tco/comprehensive?lifecycle_years=${lifecycleYears}`);
}

export async function fetchROISummary(lifecycleYears = 5, _auth?: AuthState | null): Promise<ROISummary> {
  return request<ROISummary>(`/tco/roi/summary?lifecycle_years=${lifecycleYears}`);
}

export async function fetchROIDashboard(lifecycleYears = 5, _auth?: AuthState | null): Promise<ROIDashboard> {
  return request<ROIDashboard>(`/tco/roi/dashboard?lifecycle_years=${lifecycleYears}`);
}

export async function fetchCostConfiguration(_auth?: AuthState | null): Promise<CostConfiguration> {
  return request<CostConfiguration>('/tco/config');
}

export async function fetchRefreshPlanning(
  maxHealthScore?: number,
  minAge?: number,
  _auth?: AuthState | null
): Promise<{ devices: Device[]; total: number }> {
  let path = '/tco/refresh-planning?page_size=100';
  if (maxHealthScore !== undefined) {
    path += `&max_health_score=${maxHealthScore}`;
  }
  if (minAge !== undefined) {
    path += `&min_age=${minAge}`;
  }
  return request<{ devices: Device[]; total: number }>(path);
}

// Health History Types
export interface HealthHistoryDataPoint {
  time: string;
  score: number;
  status: 'healthy' | 'warning' | 'critical' | 'stale';
  consecutive_healthy: number;
}

export interface HealthHistoryResponse {
  uuid: string;
  start: string;
  stop: string;
  data_points: HealthHistoryDataPoint[];
  count: number;
}

export async function fetchHealthHistory(
  uuid: string,
  start: string = '-7d',
  stop: string = 'now()',
  raw: boolean = false,
  _auth?: AuthState | null
): Promise<HealthHistoryResponse> {
  let path = `/health/${uuid}/history?start=${encodeURIComponent(start)}&stop=${encodeURIComponent(stop)}`;
  if (raw) {
    path += '&raw=true';
  }
  return request<HealthHistoryResponse>(path);
}

// Metrics History Types
export interface MetricsHistoryDataPoint {
  time: string;
  battery_health?: number;
  battery_percent?: number;
  cycle_count?: number;
  cpu_system?: number;
  cpu_user?: number;
  cpu_idle?: number;
  memory_percent?: number;
  memory_compressed_mb?: number;
  memory_swap_ins?: number;
  memory_swap_outs?: number;
  swap_percent?: number;
  // Memory pressure metrics (from @jamf/memory)
  pressure_pct_mean?: number;    // Mean memory pressure percentage
  pressure_pct_min?: number;     // Min memory pressure percentage
  pressure_pct_max?: number;     // Max memory pressure percentage
  pressure_state_max?: number;   // Max pressure state: 1=normal, 2=warn, 3=urgent, 4=critical
  // Disk metrics (from @jamf/disk)
  disk_percent?: number;         // Used percentage
  disk_total_gb?: number;        // Total disk capacity in decimal GB
  disk_available_gb?: number;    // Available space including purgeable (matches macOS System Settings)
  disk_purgeable_gb?: number;    // Purgeable space macOS can reclaim
  disk_free_gb?: number;         // Raw free space (excludes purgeable)
  disk_used_gb?: number;         // Used disk space
  storage_free?: number;         // Calculated: (available_gb / total_gb) * 100
  load_average?: number;
  temperature?: number;
  fan_percent?: number;
  network_in?: number;
  network_out?: number;
  power_ior?: number;
  power_smc?: number;
  // Sleep events (for gap correlation - shows if device was sleeping vs truly offline)
  sleep_events?: number;         // Total sleep events in this time bucket
  dark_wake_count?: number;      // System maintenance wakes (device is sleeping but online)
  full_wake_count?: number;      // User wakes (device is actively being used)
}

export interface MetricsHistoryResponse {
  uuid: string;
  start: string;
  stop: string;
  window: string;
  data_points: MetricsHistoryDataPoint[];
  count: number;
}

export async function fetchMetricsHistory(
  uuid: string,
  start: string = '-7d',
  stop: string = 'now()',
  raw: boolean = false,
  _auth?: AuthState | null
): Promise<MetricsHistoryResponse> {
  let path = `/health/${uuid}/metrics-history?start=${encodeURIComponent(start)}&stop=${encodeURIComponent(stop)}`;
  if (raw) {
    path += '&raw=true';
  }
  return request<MetricsHistoryResponse>(path);
}

export interface WifiTelemetryPoint {
  timestamp: string;
  rssi_mean: number;
  rssi_min: number | null;
  rssi_max: number | null;
  noise_mean: number;
  noise_min: number | null;
  noise_max: number | null;
  transmit_rate_mean: number;
  channel: string;
  channel_band: string;
  channel_width: string;
}

export interface FanTelemetryPoint {
  timestamp: string;
  fan: string;
  percent_mean: number;
  percent_min: number;
  percent_max: number;
}

export async function fetchWifiTelemetry(uuid: string, timeRange = '-7d', _auth?: AuthState | null): Promise<WifiTelemetryPoint[]> {
  return request<WifiTelemetryPoint[]>(`/telemetry/wifi?uuid=${encodeURIComponent(uuid)}&time_range=${encodeURIComponent(timeRange)}`);
}

export async function fetchFanTelemetry(uuid: string, timeRange = '-7d', _auth?: AuthState | null): Promise<FanTelemetryPoint[]> {
  return request<FanTelemetryPoint[]>(`/telemetry/fan-speed?uuid=${encodeURIComponent(uuid)}&time_range=${encodeURIComponent(timeRange)}`);
}

export interface PowerTelemetryPoint {
  timestamp: string;
  smc_watts?: number;   // From @jamf/power_smc value_mean
  ior_watts?: number;   // From @jamf/power_ior watts_mean (total)
  // IOR component breakdown
  cpu_watts?: number;   // From @jamf/power_ior cpu_watts_mean
  gpu_watts?: number;   // From @jamf/power_ior gpu_watts_mean
  ane_watts?: number;   // From @jamf/power_ior ane_watts_mean (Apple Neural Engine)
}

export async function fetchPowerTelemetry(uuid: string, timeRange = '-7d', _auth?: AuthState | null): Promise<PowerTelemetryPoint[]> {
  return request<PowerTelemetryPoint[]>(`/telemetry/power?uuid=${encodeURIComponent(uuid)}&time_range=${encodeURIComponent(timeRange)}`);
}

export interface NetworkTelemetryPoint {
  timestamp: string;
  interface: string;  // Network interface (en0, en1, etc.)
  ibytes_mean: number;
  idrops_mean: number;
  ierrors_mean: number;
  ipackets_mean: number;
  obytes_mean: number;
  odrops_mean: number;
  oerrors_mean: number;
  opackets_mean: number;
}

export async function fetchNetworkTelemetry(uuid: string, timeRange = '-7d', _auth?: AuthState | null): Promise<NetworkTelemetryPoint[]> {
  return request<NetworkTelemetryPoint[]>(`/telemetry/network?uuid=${encodeURIComponent(uuid)}&time_range=${encodeURIComponent(timeRange)}`);
}

// Application Recovery Types
export interface AppRecoverySummary {
  total_tracked_apps: number;
  total_installations: number;
  unused_installations: number;
  unused_percentage: number;
  total_annual_license_cost: number;
  recoverable_annual_cost: number;
  potential_savings_percentage: number;
  top_recovery_opportunities: { app_name: string; recoverable_cost: number }[];
}

export interface AppRecoveryByApplication {
  app_name: string;
  vendor: string;
  license_type: string;
  annual_cost_per_license: number;
  total_installations: number;
  active_installations: number;
  unused_installations: number;
  unused_percentage: number;
  total_annual_cost: number;
  recoverable_cost: number;
}

export interface AppRecoveryByDepartment {
  department: string;
  total_installations: number;
  unused_installations: number;
  unused_percentage: number;
  total_annual_cost: number;
  recoverable_cost: number;
}

export interface UnusedAppInstallation {
  device_uuid: string;
  device_name: string;
  user_name: string;
  department: string;
  app_name: string;
  version: string;
  last_used_date: string | null;
  days_since_used: number;
  annual_license_cost: number;
  recovery_priority: string;
}

export interface AppRecoveryConfig {
  unused_threshold_days: number;
  tracked_applications: {
    app_name: string;
    vendor: string;
    license_type: string;
    annual_cost_per_license: number;
    is_tracked: boolean;
  }[];
}

export interface AppRecoveryDashboard {
  summary: AppRecoverySummary;
  by_application: AppRecoveryByApplication[];
  by_department: AppRecoveryByDepartment[];
  unused_installations: UnusedAppInstallation[];
  config: AppRecoveryConfig;
}

export async function fetchAppRecoveryDashboard(_auth?: AuthState | null): Promise<AppRecoveryDashboard> {
  return request<AppRecoveryDashboard>('/tco/app-recovery/dashboard');
}

export async function fetchAppRecoverySummary(_auth?: AuthState | null): Promise<AppRecoverySummary> {
  return request<AppRecoverySummary>('/tco/app-recovery/summary');
}

export async function fetchUnusedInstallations(limit = 100, _auth?: AuthState | null): Promise<UnusedAppInstallation[]> {
  return request<UnusedAppInstallation[]>(`/tco/app-recovery/unused?limit=${limit}`);
}

// Hardware Fit Analysis Types
export type FitStatus = 'optimal' | 'over_speced' | 'under_speced';
export type DeviceTier = 'entry' | 'standard' | 'pro' | 'workstation';

export interface ResourcePressure {
  cpu_avg: number | null;
  cpu_p95: number | null;
  memory_avg: number | null;
  memory_p95: number | null;
  storage_used_pct: number | null;
  swap_events_daily: number | null;
  thermal_throttle_pct: number | null;
}

export interface FitRecommendation {
  device_uuid: string;
  device_name: string;
  user_name: string;
  department: string;
  current_model: string;
  current_tier: DeviceTier;
  fit_status: FitStatus;
  recommended_tier: DeviceTier | null;
  recommended_models: string[];
  reason: string;
  confidence: number;
  potential_savings: number | null;
  productivity_impact: string | null;
  resource_pressure: ResourcePressure;
}

export interface HardwareFitSummary {
  total_devices: number;
  optimal_count: number;
  over_speced_count: number;
  under_speced_count: number;
  optimal_pct: number;
  over_speced_pct: number;
  under_speced_pct: number;
  total_potential_savings: number;
  productivity_at_risk_count: number;
}

export interface FitByDepartment {
  department: string;
  device_count: number;
  optimal_count: number;
  over_speced_count: number;
  under_speced_count: number;
  potential_savings: number;
}

export interface FitByTier {
  tier: DeviceTier;
  device_count: number;
  optimal_count: number;
  over_speced_count: number;
  under_speced_count: number;
  avg_cpu_usage: number;
  avg_memory_usage: number;
}

export interface HardwareFitDashboard {
  summary: HardwareFitSummary;
  over_speced_devices: FitRecommendation[];
  under_speced_devices: FitRecommendation[];
  by_department: FitByDepartment[];
  by_tier: FitByTier[];
}

export async function fetchHardwareFitDashboard(_auth?: AuthState | null): Promise<HardwareFitDashboard> {
  return request<HardwareFitDashboard>('/tco/hardware-fit/dashboard');
}

export async function fetchHardwareFitSummary(_auth?: AuthState | null): Promise<HardwareFitSummary> {
  return request<HardwareFitSummary>('/tco/hardware-fit/summary');
}

// Hardware Pricing Configuration Types
export interface TierDevice {
  model: string;
  type: 'Laptop' | 'Desktop';
  cpu: string;
  memory_gb: number;
  storage_amount: number;
  storage_unit: 'GB' | 'TB';
  msrp: number;
  lifecycle_years: number;
}

export interface TierCatalogEntry {
  name: string;
  description: string;
  avg_msrp: number;
  devices: TierDevice[];
}

export interface DepartmentStandard {
  default_tier: DeviceTier;
  recommended_tiers: DeviceTier[];
}

export interface HardwarePricingConfig {
  tier_catalog: Record<DeviceTier, TierCatalogEntry>;
  department_standards: Record<string, DepartmentStandard>;
}

export async function fetchHardwarePricingConfig(_auth?: AuthState | null): Promise<HardwarePricingConfig> {
  return request<HardwarePricingConfig>('/tco/hardware-fit/pricing-config');
}

// Model names mapping (model identifier -> marketing name)
export type ModelNamesMap = Record<string, string>;

export async function fetchModelNames(_auth?: AuthState | null): Promise<ModelNamesMap> {
  return request<ModelNamesMap>('/tco/hardware-fit/model-names');
}

// =============================================================================
// User Preferences
// =============================================================================

export interface UserPreferences {
  user_id: string;
  email: string | null;
  display_name: string | null;
  currency: string;
  theme: 'light' | 'dark' | 'system';
  locale: string;
  timezone: string;
  preferences: Record<string, unknown>;
}

export interface UserPreferencesUpdate {
  email?: string;
  display_name?: string;
  currency?: string;
  theme?: 'light' | 'dark' | 'system';
  locale?: string;
  timezone?: string;
  preferences?: Record<string, unknown>;
}

export interface UserDefaultsFromAuth {
  userId: string;
  email?: string;
  name?: string;
  locale?: string;
  timezone?: string;
  theme?: string;
}

/**
 * Fetch user preferences from the backend.
 * If no preferences exist, creates them with defaults from Okta token.
 */
export async function fetchUserPreferences(defaults: UserDefaultsFromAuth, _auth?: AuthState | null): Promise<UserPreferences> {
  const headers: Record<string, string> = {
    'X-User-ID': defaults.userId,
  };

  // Pass Okta defaults for first-time user creation
  if (defaults.email) headers['X-User-Email'] = defaults.email;
  if (defaults.name) headers['X-User-Name'] = defaults.name;
  if (defaults.locale) headers['X-Default-Locale'] = defaults.locale;
  if (defaults.timezone) headers['X-Default-Timezone'] = defaults.timezone;
  if (defaults.theme) headers['X-Default-Theme'] = defaults.theme;

  return request<UserPreferences>('/user/preferences', { headers });
}

/**
 * Update user preferences (partial update).
 */
export async function updateUserPreferences(
  userId: string,
  updates: UserPreferencesUpdate,
  _auth?: AuthState | null
): Promise<UserPreferences> {
  return request<UserPreferences>('/user/preferences', {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-User-ID': userId,
    },
    body: JSON.stringify(updates),
  });
}

// =============================================================================
// Device Overrides (user-editable device metadata)
// =============================================================================

export interface DeviceOverride {
  uuid: string;
  user_name: string | null;
  user_email: string | null;
  department: string | null;
  notes: string | null;
  asset_tag: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface DeviceOverrideUpdate {
  user_name?: string | null;
  user_email?: string | null;
  department?: string | null;
  notes?: string | null;
  asset_tag?: string | null;
  clear_fields?: string[];
}

export type DataSource = 'jamf_pro' | 'manual' | null;

export interface DeviceInfo {
  uuid: string;
  hostname: string | null;
  serial_number: string | null;
  hardware_model: string | null;
  os_version: string | null;
  os_build: string | null;
  arch: string | null;
  processor: string | null;
  cpu_cores: number | null;
  cpu_p_cores: number | null;
  cpu_e_cores: number | null;
  memory_gb: number | null;
  disk_gb: number | null;  // Total disk storage capacity
  app_version: string | null;
  jamf_id: number | null;  // Jamf Pro computer ID (JSSID)
  // Fields with source tracking
  user_name: string | null;
  user_name_source: DataSource;
  user_email: string | null;
  user_email_source: DataSource;
  department: string | null;
  department_source: DataSource;
  position: string | null;  // Job title/role from Jamf Pro (read-only)
  notes: string | null;
  asset_tag: string | null;
  asset_tag_source: DataSource;
  // Warranty data from ABM/ASM
  device_age_years: number | null;
  warranty_status: 'active' | 'expiring_soon' | 'expired' | 'unknown' | null;
  warranty_end_date: string | null;
  has_applecare: boolean | null;
  purchase_date: string | null;
  warranty_last_synced: string | null;
  // Metadata
  has_overrides: boolean;
  override_updated_at: string | null;
  override_updated_by: string | null;
}

export interface AuditLogEntry {
  id: number;
  timestamp: string | null;
  user_id: string | null;
  user_email: string | null;
  action: 'create' | 'update' | 'delete';
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
}

/**
 * Fetch complete device information including overrides.
 */
export async function fetchDeviceInfo(uuid: string, _auth?: AuthState | null): Promise<DeviceInfo> {
  return request<DeviceInfo>(`/device/${uuid}`);
}

/**
 * Fetch just the override data for a device.
 */
export async function fetchDeviceOverrides(uuid: string, _auth?: AuthState | null): Promise<DeviceOverride | null> {
  const data = await request<DeviceOverride | null>(`/device/${uuid}/overrides`);
  return data || null;
}

/**
 * Update device override data.
 */
export async function updateDeviceOverrides(
  uuid: string,
  updates: DeviceOverrideUpdate,
  _auth?: AuthState | null
): Promise<DeviceOverride> {
  return request<DeviceOverride>(`/device/${uuid}/overrides`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

/**
 * Delete all override data for a device.
 */
export async function deleteDeviceOverrides(uuid: string, _auth?: AuthState | null): Promise<void> {
  await request<void>(`/device/${uuid}/overrides`, { method: 'DELETE' });
}

/**
 * Get audit history for device overrides.
 */
export async function fetchDeviceAuditHistory(uuid: string, limit = 50, _auth?: AuthState | null): Promise<AuditLogEntry[]> {
  return request<AuditLogEntry[]>(`/device/${uuid}/audit-history?limit=${limit}`);
}

/**
 * Get list of all departments with overrides.
 */
export async function fetchDepartmentsList(_auth?: AuthState | null): Promise<string[]> {
  const data = await request<{ departments: string[] }>('/device/meta/departments');
  return data.departments || [];
}

// =============================================================================
// Bulk Warranty Data
// =============================================================================

export interface DeviceWarrantyData {
  device_uuid: string;
  serial_number: string | null;
  device_age_years: number | null;
  warranty_status: 'active' | 'expiring_soon' | 'expired' | 'unknown' | null;
  warranty_end_date: string | null;
  has_applecare: boolean | null;
  purchase_date: string | null;
  last_synced_at: string | null;
}

export interface BulkWarrantyResponse {
  devices: Record<string, DeviceWarrantyData>;
  found_count: number;
  requested_count: number;
}

/**
 * Fetch warranty and age data for multiple devices.
 * Returns cached data from Apple Business/School Manager sync.
 */
export async function fetchBulkWarrantyData(
  uuids: string[],
  _auth?: AuthState | null
): Promise<Record<string, DeviceWarrantyData>> {
  if (uuids.length === 0) return {};

  try {
    const data = await request<BulkWarrantyResponse>('/device/warranty/bulk', {
      method: 'POST',
      body: JSON.stringify({ uuids }),
    });
    return data.devices;
  } catch {
    console.warn('Failed to fetch bulk warranty data');
    return {};
  }
}

// =============================================================================
// Health Threshold Configuration
// =============================================================================

export interface HealthThreshold {
  metric: string;
  label: string;
  unit: string;
  description: string | null;
  warning_value: number | null;
  critical_value: number | null;
  direction: 'above' | 'below';
  weight: number;
  enabled: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

export interface HealthThresholdUpdate {
  label?: string;
  unit?: string;
  description?: string;
  warning_value?: number;
  critical_value?: number;
  direction?: 'above' | 'below';
  weight?: number;
  enabled?: boolean;
}

export interface HealthThresholdsResponse {
  status: string;
  thresholds: HealthThreshold[];
  count: number;
  enabled_count: number;
}

/**
 * Fetch all health thresholds from the backend.
 */
export async function fetchHealthThresholds(_auth?: AuthState | null): Promise<HealthThreshold[]> {
  const data = await request<HealthThresholdsResponse>('/health/config/thresholds');
  return data.thresholds;
}

/**
 * Fetch a single health threshold by metric name.
 */
export async function fetchHealthThreshold(metric: string, _auth?: AuthState | null): Promise<HealthThreshold> {
  const data = await request<{ threshold: HealthThreshold }>(`/health/config/thresholds/${encodeURIComponent(metric)}`);
  return data.threshold;
}

/**
 * Update a health threshold. Changes are persisted to database and
 * take effect immediately for new health evaluations.
 */
export async function updateHealthThreshold(
  metric: string,
  updates: HealthThresholdUpdate,
  _auth?: AuthState | null
): Promise<HealthThreshold> {
  const data = await request<{ threshold: HealthThreshold }>(`/health/config/thresholds/${encodeURIComponent(metric)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  return data.threshold;
}

/**
 * Reload health configuration from database.
 * Call this after making multiple threshold updates to ensure all services are in sync.
 */
export async function reloadHealthConfig(_auth?: AuthState | null): Promise<{ threshold_count: number }> {
  return request<{ threshold_count: number }>('/health/config/reload', { method: 'POST' });
}

// ============================================================================
// Staleness Detection API
// ============================================================================

export interface StalenessConfig {
  stale_threshold_days: number;
  warning_threshold_days: number;
  enabled: boolean;
  affects_health_score: boolean;
  stale_score_penalty: number;
  // Auto-delete settings
  auto_delete_enabled: boolean;
  auto_delete_after_days: number;
  // Alerting settings
  alert_on_stale: boolean;
  alert_on_warning: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

export interface StalenessConfigUpdate {
  stale_threshold_days?: number;
  warning_threshold_days?: number;
  enabled?: boolean;
  affects_health_score?: boolean;
  stale_score_penalty?: number;
  // Auto-delete settings
  auto_delete_enabled?: boolean;
  auto_delete_after_days?: number;
  // Alerting settings
  alert_on_stale?: boolean;
  alert_on_warning?: boolean;
}

export interface StalenessCounts {
  stale: number;
  warning: number;
  active: number;
  unknown: number;
  total: number;
}

export interface StalenessSummary {
  status: string;
  enabled: boolean;
  config?: {
    stale_threshold_days: number;
    warning_threshold_days: number;
  };
  counts?: StalenessCounts;
  message?: string;
}

export interface StaleDevice {
  uuid: string;
  hostname: string;
  last_seen: string | null;
  days_since_last_seen: number | null;
  staleness_status: 'stale' | 'warning' | 'active' | 'unknown';
  health_status: string;
  composite_score: number | null;
}

export interface StaleDevicesResponse {
  status: string;
  enabled: boolean;
  devices: StaleDevice[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  stale_count: number;
  warning_count: number;
  message?: string;
}

export interface DeviceStalenessStatus {
  uuid: string;
  last_seen: string | null;
  days_since_last_seen: number | null;
  is_stale: boolean;
  is_warning: boolean;
  status: 'active' | 'warning' | 'stale' | 'unknown';
}

/**
 * Fetch staleness detection configuration.
 */
export async function fetchStalenessConfig(_auth?: AuthState | null): Promise<StalenessConfig> {
  const data = await request<{ config: StalenessConfig }>('/health/staleness/config');
  return data.config;
}

/**
 * Update staleness detection configuration.
 */
export async function updateStalenessConfig(updates: StalenessConfigUpdate, _auth?: AuthState | null): Promise<StalenessConfig> {
  const data = await request<{ config: StalenessConfig }>('/health/staleness/config', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  return data.config;
}

/**
 * Fetch staleness summary with counts.
 */
export async function fetchStalenessSummary(_auth?: AuthState | null): Promise<StalenessSummary> {
  return request<StalenessSummary>('/health/staleness/summary');
}

/**
 * Fetch stale devices list (paginated).
 */
export async function fetchStaleDevices(
  page = 1,
  pageSize = 25,
  statusFilter?: 'stale' | 'warning',
  _auth?: AuthState | null
): Promise<StaleDevicesResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  if (statusFilter) params.append('status_filter', statusFilter);

  return request<StaleDevicesResponse>(`/health/staleness/devices?${params}`);
}

// =============================================================================
// Device Lifecycle (Deletion)
// =============================================================================

export interface DeletedDevice {
  uuid: string;
  hostname: string;
  serial_number: string | null;
  hardware_model: string | null;
  deleted_at: string;
  deleted_by: string;
  deletion_reason: string | null;
  deletion_type: 'manual' | 'bulk' | 'stale_cleanup' | 'api';
  recovered_at: string | null;
  device_snapshot: Record<string, unknown>;
}

export interface DeletionConfig {
  auto_cleanup_enabled: boolean;
  auto_cleanup_stale_days: number;
  auto_cleanup_require_confirmation: boolean;
  retention_days: number;
  require_deletion_reason: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

export interface DeleteDeviceResponse {
  status: string;
  message: string;
  deleted_device: DeletedDevice;
}

/**
 * Delete a single device (soft delete).
 * Device is moved to deletion archive and can be recovered.
 */
export async function deleteDevice(uuid: string, reason?: string, _auth?: AuthState | null): Promise<DeleteDeviceResponse> {
  const params = new URLSearchParams();
  if (reason) params.append('reason', reason);

  const path = params.toString()
    ? `/devices/lifecycle/${uuid}?${params}`
    : `/devices/lifecycle/${uuid}`;

  return request<DeleteDeviceResponse>(path, { method: 'DELETE' });
}

/**
 * Get deletion configuration.
 */
export async function fetchDeletionConfig(_auth?: AuthState | null): Promise<DeletionConfig> {
  const data = await request<{ config: DeletionConfig }>('/devices/lifecycle/config');
  return data.config;
}

/**
 * Get list of deleted devices.
 */
export async function fetchDeletedDevices(
  page = 1,
  pageSize = 25,
  deletionType?: string,
  includeRecovered = false,
  _auth?: AuthState | null
): Promise<{ devices: DeletedDevice[]; total: number; page: number; page_size: number; total_pages: number }> {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
    include_recovered: includeRecovered.toString(),
  });
  if (deletionType) params.append('deletion_type', deletionType);

  return request<{ devices: DeletedDevice[]; total: number; page: number; page_size: number; total_pages: number }>(`/devices/lifecycle/deleted?${params}`);
}

// =============================================================================
// Events
// =============================================================================

export interface Event {
  id?: string;
  timestamp: string;
  type?: string;
  event_type?: string;
  severity: string;
  message: string;
  device_uuid?: string;
  device_name?: string;
  hostname?: string;
  metadata?: Record<string, unknown>;
}

export interface EventsResponse {
  events: Event[];
  total: number;
}

/**
 * Fetch events with optional limit.
 */
export async function fetchEvents(limit = 100, _auth?: AuthState | null): Promise<EventsResponse> {
  return request<EventsResponse>(`/events?limit=${limit}`);
}

// =============================================================================
// Alerts (Server-side health status events)
// =============================================================================

export interface AlertEvent {
  uuid: string;
  alert_id: string;
  severity: string;
  category: string;
  message: string;
  timestamp: string;
  acknowledged: boolean;
  hostname?: string;
}

export interface AlertsResponse {
  alerts: AlertEvent[];
  total: number;
}

/**
 * Fetch alerts (server-side health status change events).
 */
export async function fetchAlerts(
  options?: { uuid?: string; severity?: string; timeRange?: string },
  _auth?: AuthState | null
): Promise<AlertsResponse> {
  const params = new URLSearchParams();
  if (options?.uuid) params.append('uuid', options.uuid);
  if (options?.severity) params.append('severity', options.severity);
  if (options?.timeRange) params.append('time_range', options.timeRange);
  const queryString = params.toString() ? `?${params}` : '';
  return request<AlertsResponse>(`/events/alerts${queryString}`);
}

// =============================================================================
// Crashes (Application crash events)
// =============================================================================

export interface CrashEvent {
  uuid: string;
  app_name: string;
  crash_reason: string;
  stack_trace: string;
  timestamp: string;
  hostname?: string;
  exception_type?: string;
  termination_namespace?: string;
  termination_indicator?: string;
}

export interface CrashesResponse {
  crashes: CrashEvent[];
  total: number;
}

/**
 * Fetch crashes for a device or fleet.
 */
export async function fetchCrashes(
  options?: { uuid?: string; identifier?: string; timeRange?: string; limit?: number },
  _auth?: AuthState | null
): Promise<CrashesResponse> {
  const params = new URLSearchParams();
  if (options?.uuid) params.append('uuid', options.uuid);
  if (options?.identifier) params.append('identifier', options.identifier);
  if (options?.timeRange) params.append('time_range', options.timeRange);
  if (options?.limit) params.append('limit', options.limit.toString());
  const queryString = params.toString() ? `?${params}` : '';
  return request<CrashesResponse>(`/events/crashes${queryString}`);
}

// =============================================================================
// Integrations
// =============================================================================

export interface JamfProConfig {
  url?: string;
  client_id?: string;
  client_secret?: string;
  enabled?: boolean;
}

export interface WebhookSettings {
  enabled: boolean;
  cooldown_seconds: number;
}

export interface Webhook {
  id: string;
  url: string;
  name?: string;
  enabled: boolean;
  events?: string[];
}

export interface ABMConfig {
  key_id?: string;
  client_id?: string;
  team_id?: string;
  private_key?: string;
}

export interface ABMStatus {
  configured: boolean;
  connected: boolean;
  last_sync?: string;
  error?: string;
}

export interface WarrantyCacheStats {
  cache: {
    total_cached: number;
    by_status: {
      active: number;
      expiring_soon: number;
      expired: number;
      unknown: number;
    };
    with_applecare: number;
    oldest_sync: string | null;
    newest_sync: string | null;
  };
  queue: {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    total: number;
  };
  sync_in_progress: boolean;
}

export interface WarrantySyncResult {
  success: boolean;
  stats?: {
    devices_checked: number;
    devices_found: number;
    devices_not_found: number;
    devices_skipped: number;
    errors: number;
    connections_used: string[];
  };
  error?: string;
}

export interface WarrantySummary {
  total_devices: number;
  warranty_coverage: {
    active: number;
    expiring_soon: number;
    expired: number;
    unknown: number;
  };
  applecare_coverage: {
    with_applecare: number;
    without_applecare: number;
  };
  sync_status: {
    last_sync: string | null;
    cached_count: number;
    sync_in_progress: boolean;
  };
}

/**
 * Fetch Jamf Pro integration config.
 */
export async function fetchJamfProConfig(_auth?: AuthState | null): Promise<JamfProConfig> {
  return request<JamfProConfig>('/integrations/jamf-pro/config');
}

/**
 * Save Jamf Pro integration config.
 */
export async function saveJamfProConfig(config: JamfProConfig, _auth?: AuthState | null): Promise<JamfProConfig> {
  return request<JamfProConfig>('/integrations/jamf-pro/config', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

/**
 * Test Jamf Pro connection.
 */
export async function testJamfProConnection(_auth?: AuthState | null): Promise<{ success: boolean; message?: string }> {
  return request<{ success: boolean; message?: string }>('/integrations/jamf-pro/test', { method: 'POST' });
}

/**
 * Trigger Jamf Pro sync.
 */
export async function triggerJamfProSync(_auth?: AuthState | null): Promise<void> {
  await request<void>('/integrations/jamf-pro/sync', { method: 'POST' });
}

/**
 * Fetch departments from Jamf Pro servers.
 */
export interface JamfProDepartmentsResponse {
  departments: string[];
  count: number;
  servers_queried: number;
}

export async function fetchJamfProDepartments(_auth?: AuthState | null): Promise<JamfProDepartmentsResponse> {
  return request<JamfProDepartmentsResponse>('/integrations/jamf-pro/departments');
}

/**
 * Fetch webhook settings.
 */
export async function fetchWebhookSettings(_auth?: AuthState | null): Promise<WebhookSettings> {
  return request<WebhookSettings>('/alerts/webhooks-settings');
}

/**
 * Save webhook settings.
 */
export async function saveWebhookSettings(settings: WebhookSettings, _auth?: AuthState | null): Promise<WebhookSettings> {
  return request<WebhookSettings>('/alerts/webhooks-settings', {
    method: 'POST',
    body: JSON.stringify(settings),
  });
}

/**
 * Fetch webhooks list.
 */
export async function fetchWebhooks(_auth?: AuthState | null): Promise<Webhook[]> {
  return request<Webhook[]>('/alerts/webhooks');
}

/**
 * Create a webhook.
 */
export async function createWebhook(webhook: Partial<Webhook>, _auth?: AuthState | null): Promise<Webhook> {
  return request<Webhook>('/alerts/webhooks', {
    method: 'POST',
    body: JSON.stringify(webhook),
  });
}

/**
 * Test a webhook.
 */
export async function testWebhook(webhookId: string, _auth?: AuthState | null): Promise<{ success: boolean; message?: string }> {
  return request<{ success: boolean; message?: string }>('/alerts/test-webhook', {
    method: 'POST',
    body: JSON.stringify({ webhook_id: webhookId }),
  });
}

/**
 * Configure ABM (Apple Business Manager).
 */
export async function configureABM(config: ABMConfig, _auth?: AuthState | null): Promise<{ status: string }> {
  return request<{ status: string }>('/warranty/configure', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

/**
 * Fetch ABM status.
 */
export async function fetchABMStatus(_auth?: AuthState | null): Promise<ABMStatus> {
  return request<ABMStatus>('/warranty/status');
}

/**
 * Fetch warranty cache statistics.
 */
export async function fetchWarrantyCacheStats(_auth?: AuthState | null): Promise<WarrantyCacheStats> {
  return request<WarrantyCacheStats>('/warranty/cache/stats');
}

/**
 * Fetch warranty summary.
 */
export async function fetchWarrantySummary(_auth?: AuthState | null): Promise<WarrantySummary> {
  return request<WarrantySummary>('/warranty/summary');
}

/**
 * Sync all device warranties from ABM/ASM.
 */
export async function syncAllWarranties(forceRefresh = false, _auth?: AuthState | null): Promise<WarrantySyncResult> {
  return request<WarrantySyncResult>('/warranty/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ force_refresh: forceRefresh }),
  });
}

/**
 * Sync warranty for a single device.
 */
export async function syncDeviceWarranty(serialNumber: string, deviceUuid?: string, _auth?: AuthState | null): Promise<{ success: boolean; serial_number: string; found?: boolean; message?: string }> {
  return request<{ success: boolean; serial_number: string; found?: boolean; message?: string }>('/warranty/sync/device', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ serial_number: serialNumber, device_uuid: deviceUuid }),
  });
}

// =============================================================================
// Access / Permissions
// =============================================================================

export interface UserAccess {
  user_id: string;
  email: string;
  role: string;
  scopes: string[];
  tenant_id: string;
}

export interface RoleAssignment {
  email: string;
  role: string;
  assigned_by?: string;
}

/**
 * Fetch current user's access/permissions.
 */
export async function fetchMyAccess(_auth?: AuthState | null): Promise<UserAccess> {
  return request<UserAccess>('/access/me');
}

/**
 * Create or update role assignment.
 */
export async function saveRoleAssignment(assignment: RoleAssignment, _auth?: AuthState | null): Promise<RoleAssignment> {
  return request<RoleAssignment>('/access/assignments', {
    method: 'POST',
    body: JSON.stringify(assignment),
  });
}

// App Usage Types
export interface AppUsageRecord {
  app_name: string;
  bundle_id: string;
  foreground_seconds: number;
  foreground_hours: number;
  launch_count: number;
  last_active: string | null;
  sample_count: number;
  sparkline?: number[];
}

export interface DeviceUtilization {
  idle_seconds: number;
  idle_hours: number;
  active_seconds: number;
  active_hours: number;
  idle_percentage: number;
}

export interface AppUsageResponse {
  uuid: string;
  time_range: string;
  apps: AppUsageRecord[];
  count: number;
  utilization: DeviceUtilization | null;
}

export async function fetchAppUsage(uuid: string, timeRange = '-7d', _auth?: AuthState | null): Promise<AppUsageResponse> {
  return request<AppUsageResponse>(`/telemetry/app-usage?uuid=${encodeURIComponent(uuid)}&time_range=${encodeURIComponent(timeRange)}`);
}

// App Usage Trends Types (Fleet-wide)
export interface AppUsageTrend {
  app_name: string;
  bundle_id: string;
  trend: 'rising' | 'stable' | 'declining' | 'zero';
  trend_pct: number;
  sparkline: number[];
  signals: ('zero' | 'minimal' | 'declining')[];
  total_foreground_seconds: number;
  device_count: number;
}

export interface AppUsageTrendsResponse {
  trends: AppUsageTrend[];
  period_days: number;
  generated_at: string;
}

export async function fetchAppUsageTrends(days = 30, _auth?: AuthState | null): Promise<AppUsageTrendsResponse> {
  return request<AppUsageTrendsResponse>(`/tco/app-recovery/usage-trends?days=${days}`);
}

// Process Types
export interface ProcessRecord {
  process_name: string;
  avg_cpu_user: number;
  avg_cpu_system: number;
  avg_cpu_total: number;
  max_cpu_user: number;
  max_cpu_system: number;
  avg_memory_mb: number;
  max_memory_mb: number;
  total_disk_read_mb: number;
  total_disk_written_mb: number;
  sample_count: number;
}

export interface ProcessesResponse {
  uuid: string;
  time_range: string;
  processes: ProcessRecord[];
  count: number;
}

export async function fetchProcesses(uuid: string, timeRange = '-24h', _auth?: AuthState | null): Promise<ProcessesResponse> {
  return request<ProcessesResponse>(`/telemetry/processes?uuid=${encodeURIComponent(uuid)}&time_range=${encodeURIComponent(timeRange)}`);
}

// =============================================================================
// Trendlines Types
// =============================================================================

export interface TrendlinesSummary {
  data_points: number;
  devices_tracked: number;
  last_updated: string | null;
  time_range: string;
}

export interface OSVersionTrendPoint {
  date: string;
  os_version: string;
  count: number;
}

export interface ModelTrendPoint {
  date: string;
  model: string;
  count: number;
}

export interface BatteryHealthTrendPoint {
  date: string;
  bucket: string;
  count: number;
}

export interface FleetHealthTrendPoint {
  date: string;
  status: string;
  count: number;
}

export interface PerformanceTrendPoint {
  date: string;
  cpu_avg: number | null;
  memory_avg: number | null;
  disk_avg: number | null;
}

export interface StabilityTrendPoint {
  date: string;
  event_type: string;
  count: number;
}

export interface TrendlinesResponse<T> {
  time_range: string;
  data: T[];
  count: number;
}

// =============================================================================
// Trendlines API Functions
// =============================================================================

export async function fetchTrendlinesSummary(
  timeRange = '-30d',
  _auth?: AuthState | null
): Promise<TrendlinesSummary> {
  return request<TrendlinesSummary>(`/trendlines/summary?time_range=${encodeURIComponent(timeRange)}`);
}

export async function fetchOSVersionTrends(
  timeRange = '-30d',
  _auth?: AuthState | null
): Promise<TrendlinesResponse<OSVersionTrendPoint>> {
  return request<TrendlinesResponse<OSVersionTrendPoint>>(`/trendlines/os-versions?time_range=${encodeURIComponent(timeRange)}`);
}

export async function fetchModelTrends(
  timeRange = '-30d',
  limit = 10,
  _auth?: AuthState | null
): Promise<TrendlinesResponse<ModelTrendPoint>> {
  return request<TrendlinesResponse<ModelTrendPoint>>(`/trendlines/models?time_range=${encodeURIComponent(timeRange)}&limit=${limit}`);
}

export async function fetchBatteryHealthTrends(
  timeRange = '-30d',
  _auth?: AuthState | null
): Promise<TrendlinesResponse<BatteryHealthTrendPoint>> {
  return request<TrendlinesResponse<BatteryHealthTrendPoint>>(`/trendlines/battery?time_range=${encodeURIComponent(timeRange)}`);
}

export async function fetchFleetHealthTrends(
  timeRange = '-30d',
  _auth?: AuthState | null
): Promise<TrendlinesResponse<FleetHealthTrendPoint>> {
  return request<TrendlinesResponse<FleetHealthTrendPoint>>(`/trendlines/health?time_range=${encodeURIComponent(timeRange)}`);
}

export async function fetchPerformanceTrends(
  timeRange = '-30d',
  _auth?: AuthState | null
): Promise<TrendlinesResponse<PerformanceTrendPoint>> {
  return request<TrendlinesResponse<PerformanceTrendPoint>>(`/trendlines/performance?time_range=${encodeURIComponent(timeRange)}`);
}

export async function fetchStabilityTrends(
  timeRange = '-30d',
  _auth?: AuthState | null
): Promise<TrendlinesResponse<StabilityTrendPoint>> {
  return request<TrendlinesResponse<StabilityTrendPoint>>(`/trendlines/stability?time_range=${encodeURIComponent(timeRange)}`);
}

// =============================================================================
// Application Intelligence API
// =============================================================================

export interface AppIntelTrackedApp {
  app_name: string;
  bundle_ids: string[];
  process_names: string[];
  vendor: string;
  annual_cost_per_license: number;
  license_type: string;
  severity: string;
  track_compliance: boolean;
  track_recovery: boolean;
  is_active: boolean;
  unused_threshold_days: number | null;
  created_at: string;
  updated_at: string;
}

export interface AppIntelDepartmentExpectation {
  department: string;
  app_name: string;
  expectation: string;
  created_at: string;
  updated_at: string;
}

export interface AppIntelDeviceOverride {
  device_uuid: string;
  app_name: string;
  override_type: string;
  reason: string;
  created_by: string | null;
  created_at: string;
}

export async function fetchAppIntelTrackedApps(_auth?: AuthState | null): Promise<AppIntelTrackedApp[]> {
  return request<AppIntelTrackedApp[]>('/app-intelligence/config/apps');
}

export async function createAppIntelTrackedApp(
  app: Partial<AppIntelTrackedApp>,
  _auth?: AuthState | null
): Promise<AppIntelTrackedApp> {
  return request<AppIntelTrackedApp>('/app-intelligence/config/apps', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(app),
  });
}

export async function updateAppIntelTrackedApp(
  appName: string,
  updates: Partial<AppIntelTrackedApp>,
  _auth?: AuthState | null
): Promise<AppIntelTrackedApp> {
  return request<AppIntelTrackedApp>(`/app-intelligence/config/apps/${encodeURIComponent(appName)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

export async function deleteAppIntelTrackedApp(appName: string, _auth?: AuthState | null): Promise<void> {
  await request<void>(`/app-intelligence/config/apps/${encodeURIComponent(appName)}`, { method: 'DELETE' });
}

export async function fetchAppIntelDepartmentExpectations(
  _auth?: AuthState | null
): Promise<AppIntelDepartmentExpectation[]> {
  return request<AppIntelDepartmentExpectation[]>('/app-intelligence/config/departments');
}

export async function createAppIntelDepartmentExpectation(
  expectation: { department: string; app_name: string; expectation: string },
  _auth?: AuthState | null
): Promise<AppIntelDepartmentExpectation> {
  return request<AppIntelDepartmentExpectation>('/app-intelligence/config/departments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(expectation),
  });
}

export async function deleteAppIntelDepartmentExpectation(
  department: string,
  appName: string,
  _auth?: AuthState | null
): Promise<void> {
  await request<void>(
    `/app-intelligence/config/departments/${encodeURIComponent(department)}/${encodeURIComponent(appName)}`,
    { method: 'DELETE' }
  );
}

export async function fetchAppIntelDeviceOverrides(_auth?: AuthState | null): Promise<AppIntelDeviceOverride[]> {
  return request<AppIntelDeviceOverride[]>('/app-intelligence/config/overrides');
}

export async function createAppIntelDeviceOverride(
  override: { device_uuid: string; app_name: string; override_type: string; reason: string },
  _auth?: AuthState | null
): Promise<AppIntelDeviceOverride> {
  return request<AppIntelDeviceOverride>('/app-intelligence/config/overrides', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(override),
  });
}

export async function deleteAppIntelDeviceOverride(
  deviceUuid: string,
  appName: string,
  _auth?: AuthState | null
): Promise<void> {
  await request<void>(
    `/app-intelligence/config/overrides/${encodeURIComponent(deviceUuid)}/${encodeURIComponent(appName)}`,
    { method: 'DELETE' }
  );
}

// =============================================================================
// Application Intelligence Dashboard
// =============================================================================

export interface AppIntelRecoverySummary {
  total_tracked_apps: number;
  apps_with_recovery: number;
  total_candidates: number;
  total_recoverable_cost: number;
  never_launched_count: number;
  never_launched_cost: number;
  by_app: { app_name: string; count: number; recoverable_cost: number }[];
  by_department: { department: string; count: number; recoverable_cost: number }[];
}

export interface AppIntelComplianceSummary {
  total_violations: number;
  by_severity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  by_app: { app_name: string; count: number; severity: string }[];
  acknowledged_count: number;
}

export interface AppIntelFleetAppUsage {
  app_name: string;
  bundle_id: string;
  device_count: number;
  active_device_count: number;
  total_usage_minutes: number;
  avg_usage_per_device: number;
  trend: string;
  by_department: Record<string, number>;
}

export interface AppIntelDashboard {
  recovery: AppIntelRecoverySummary;
  compliance: AppIntelComplianceSummary;
  top_apps: AppIntelFleetAppUsage[];
  tracked_apps_count: number;
  devices_with_issues: number;
}

export async function fetchAppIntelDashboard(_auth?: AuthState | null): Promise<AppIntelDashboard> {
  return request<AppIntelDashboard>('/app-intelligence/dashboard');
}

export interface AppIntelRecoveryCandidate {
  device_uuid: string;
  device_name: string;
  department: string;
  user_name: string | null;
  app_name: string;
  vendor: string;
  last_used: string | null;
  days_unused: number;
  annual_cost: number;
  recommendation: 'reclaim' | 'investigate' | 'retain';
  installed: boolean;
  ever_launched: boolean;
}

export async function fetchAppIntelRecoveryCandidates(
  appName?: string,
  department?: string,
  limit = 100,
  _auth?: AuthState | null
): Promise<AppIntelRecoveryCandidate[]> {
  const params = new URLSearchParams();
  if (appName) params.append('app_name', appName);
  if (department) params.append('department', department);
  params.append('limit', limit.toString());
  return request<AppIntelRecoveryCandidate[]>(`/app-intelligence/recovery/candidates?${params}`);
}

export interface AppIntelComplianceViolation {
  id: string;
  device_uuid: string;
  device_name: string;
  department: string;
  user_name: string | null;
  app_name: string;
  vendor: string;
  severity: string;
  violation_type: string;
  first_seen: string;
  last_active: string | null;
  usage_minutes: number;
  expected_rule: string;
  is_acknowledged: boolean;
  acknowledged_until: string | null;
}

export async function fetchAppIntelComplianceViolations(
  severity?: string,
  appName?: string,
  limit = 100,
  _auth?: AuthState | null
): Promise<AppIntelComplianceViolation[]> {
  const params = new URLSearchParams();
  if (severity) params.append('severity', severity);
  if (appName) params.append('app_name', appName);
  params.append('limit', limit.toString());
  return request<AppIntelComplianceViolation[]>(`/app-intelligence/compliance/violations?${params}`);
}

// Application Discovery from Jamf Pro
export interface DiscoveredApplication {
  bundle_id: string;
  name: string;
  version: string;
  path: string;
  mac_app_store: boolean;
  size_megabytes: number | null;
}

export interface AppDiscoveryResult {
  success: boolean;
  error?: string;
  device_uuid: string;
  device_name?: string;
  model?: string;
  server_id?: string;
  server_name?: string;
  application_count?: number;
  applications: DiscoveredApplication[];
}

export async function discoverDeviceApplications(
  deviceUuid: string,
  _auth?: AuthState | null
): Promise<AppDiscoveryResult> {
  return request<AppDiscoveryResult>(`/app-intelligence/discover/${encodeURIComponent(deviceUuid)}`);
}


// ── Fleet Dashboard APIs ──────────────────────────────────────────────────────

export interface FleetPowerSummary {
  total_devices: number;
  avg_smc_watts: number | null;
  avg_ior_watts: number | null;
  max_smc_watts: number | null;
  max_ior_watts: number | null;
  time_range: string;
}

export async function fetchFleetPower(timeRange = '-24h', _auth?: AuthState | null): Promise<FleetPowerSummary> {
  return request<FleetPowerSummary>(`/fleet/power?time_range=${encodeURIComponent(timeRange)}`);
}

export interface FleetProcess {
  process_name: string;
  device_count: number;
  avg_cpu_total: number;
  max_cpu_total: number;
  avg_memory_mb: number;
  max_memory_mb: number;
  total_disk_read_gb: number;
  total_disk_written_gb: number;
  sparkline?: number[];  // Daily device count over last 7 days
}

export interface FleetProcessesSummary {
  processes: FleetProcess[];
  total_devices: number;
  time_range: string;
}

export async function fetchFleetProcesses(timeRange = '-24h', limit = 10, _auth?: AuthState | null): Promise<FleetProcessesSummary> {
  return request<FleetProcessesSummary>(`/fleet/processes?time_range=${encodeURIComponent(timeRange)}&limit=${limit}`);
}

export interface FleetAppSummary {
  app_name: string;
  bundle_id: string;
  device_count: number;
  active_device_count: number;
  avg_usage_minutes: number;
  total_usage_minutes: number;
  sparkline?: number[];  // Daily usage seconds over last 7 days
}

export interface FleetAppsSummary {
  apps: FleetAppSummary[];
  total_devices: number;
  time_range: string;
}

export async function fetchFleetApps(timeRange = '-7d', limit = 10, _auth?: AuthState | null): Promise<FleetAppsSummary> {
  return request<FleetAppsSummary>(`/fleet/apps?time_range=${encodeURIComponent(timeRange)}&limit=${limit}`);
}

// ── Crash Analytics ───────────────────────────────────────────────────────────

export interface CrashRecord {
  uuid: string;
  app_name: string;        // Bundle ID / identifier
  crash_reason: string;    // Path to crashed executable
  stack_trace: string;
  timestamp: string;
}

export interface DeviceCrashesResponse {
  crashes: CrashRecord[];
  total: number;
}

export interface FleetCrash {
  identifier: string;      // Bundle ID or app identifier
  crash_count: number;
  device_count: number;
  last_crash: string | null;
  affected_users: string[];
}

export interface FleetCrashesSummary {
  crashes: FleetCrash[];
  total_crashes: number;
  total_affected_devices: number;
  time_range: string;
}

/**
 * Fetch crash records for a specific device.
 */
export async function fetchDeviceCrashes(
  uuid: string,
  timeRange = '-30d',
  limit = 100,
  _auth?: AuthState | null
): Promise<DeviceCrashesResponse> {
  const params = new URLSearchParams({
    uuid,
    time_range: timeRange,
    limit: limit.toString(),
  });
  return request<DeviceCrashesResponse>(`/events/crashes?${params}`);
}

/**
 * Fetch fleet-wide crash analytics.
 */
export async function fetchFleetCrashes(
  timeRange = '-30d',
  limit = 25,
  _auth?: AuthState | null
): Promise<FleetCrashesSummary> {
  const params = new URLSearchParams({
    time_range: timeRange,
    limit: limit.toString(),
  });
  return request<FleetCrashesSummary>(`/fleet/crashes?${params}`);
}

// =============================================================================
// Audit Logs & SIEM Forwarders
// =============================================================================

export type AuditCategory = 'authentication' | 'authorization' | 'user_management' | 'data_access' | 'data_modification' | 'configuration' | 'system' | 'security';
export type AuditSeverity = 'info' | 'low' | 'medium' | 'high' | 'critical';
export type SIEMForwarderType = 'webhook' | 'splunk_hec' | 'datadog' | 'syslog_tcp' | 'syslog_udp' | 'syslog_tls';

export interface AuditEvent {
  event_id: string;
  timestamp: string;
  category: string;
  action: string;
  severity: string;
  actor_id: string | null;
  actor_email: string | null;
  target_type: string | null;
  target_id: string | null;
  client_ip: string | null;
  tenant_id: string | null;
  description: string;
  details: Record<string, unknown> | null;
}

export interface AuditLogResponse {
  events: AuditEvent[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface AuditStats {
  total_events: number;
  events_by_category: Record<string, number>;
  events_by_severity: Record<string, number>;
  events_last_24h: number;
  forwarders_configured: number;
  forwarders_enabled: number;
  queue_depth: number;
}

export interface SIEMForwarder {
  id: string;
  name: string;
  forwarder_type: SIEMForwarderType;
  endpoint: string;
  index: string | null;
  source: string | null;
  source_type: string | null;
  enabled: boolean;
  verify_ssl: boolean;
  batch_size: number;
  timeout_seconds: number;
}

export interface SIEMForwarderCreate {
  name: string;
  forwarder_type: SIEMForwarderType;
  endpoint: string;
  auth_token?: string;
  index?: string;
  source?: string;
  source_type?: string;
  enabled?: boolean;
  verify_ssl?: boolean;
  batch_size?: number;
  timeout_seconds?: number;
}

export interface SIEMForwarderUpdate {
  name?: string;
  endpoint?: string;
  auth_token?: string;
  index?: string;
  source?: string;
  source_type?: string;
  enabled?: boolean;
  verify_ssl?: boolean;
  batch_size?: number;
  timeout_seconds?: number;
}

export async function fetchAuditLogs(
  options?: {
    page?: number;
    pageSize?: number;
    category?: string;
    severity?: string;
    actorId?: string;
    startTime?: string;
    endTime?: string;
  },
  _auth?: AuthState | null
): Promise<AuditLogResponse> {
  const params = new URLSearchParams();
  if (options?.page) params.append('page', options.page.toString());
  if (options?.pageSize) params.append('page_size', options.pageSize.toString());
  if (options?.category) params.append('category', options.category);
  if (options?.severity) params.append('severity', options.severity);
  if (options?.actorId) params.append('actor_id', options.actorId);
  if (options?.startTime) params.append('start_time', options.startTime);
  if (options?.endTime) params.append('end_time', options.endTime);

  const queryString = params.toString();
  return request<AuditLogResponse>(`/audit/logs${queryString ? `?${queryString}` : ''}`);
}

export async function fetchAuditStats(_auth?: AuthState | null): Promise<AuditStats> {
  return request<AuditStats>('/audit/stats');
}

export async function exportAuditLogs(
  format: 'json' | 'cef' | 'csv',
  options?: {
    category?: string;
    severity?: string;
    startTime?: string;
    endTime?: string;
  },
  _auth?: AuthState | null
): Promise<Blob> {
  const params = new URLSearchParams({ format });
  if (options?.category) params.append('category', options.category);
  if (options?.severity) params.append('severity', options.severity);
  if (options?.startTime) params.append('start_time', options.startTime);
  if (options?.endTime) params.append('end_time', options.endTime);

  return requestBlob(`/audit/logs/export?${params}`);
}

export async function fetchSIEMForwarders(_auth?: AuthState | null): Promise<SIEMForwarder[]> {
  return request<SIEMForwarder[]>('/audit/forwarders');
}

export async function createSIEMForwarder(
  forwarder: SIEMForwarderCreate,
  _auth?: AuthState | null
): Promise<SIEMForwarder> {
  return request<SIEMForwarder>('/audit/forwarders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(forwarder),
  });
}

export async function updateSIEMForwarder(
  id: string,
  updates: SIEMForwarderUpdate,
  _auth?: AuthState | null
): Promise<SIEMForwarder> {
  return request<SIEMForwarder>(`/audit/forwarders/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

export async function deleteSIEMForwarder(id: string, _auth?: AuthState | null): Promise<void> {
  await request<void>(`/audit/forwarders/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function testSIEMForwarder(
  id: string,
  _auth?: AuthState | null
): Promise<{ status: string; message: string }> {
  return request<{ status: string; message: string }>(`/audit/forwarders/${encodeURIComponent(id)}/test`, { method: 'POST' });
}

// ── Performance Drill-Down: Fetch Devices by Filter ──────────────────────────

export interface PerformanceDeviceList {
  uuids: string[];
  total: number;
  filter_type: 'app' | 'process' | 'crash';
  filter_value: string;
  time_range: string;
}

// ── Sleep Periods (for chart overlays) ────────────────────────────────────────

export interface SleepPeriod {
  sleep_start: string;
  wake_time: string;
  duration_ms: number;
  sleep_reason: string | null;
  wake_reason: string | null;
  wake_type: string | null;
  dark_wake_count: number;
}

export interface SleepPeriodsResponse {
  uuid: string;
  time_range: string;
  periods: SleepPeriod[];
  total_sleep_ms: number;
  total_dark_wakes: number;
}

/**
 * Fetch devices filtered by app usage, process activity, or crash identifier.
 */
export async function fetchPerformanceDevices(
  filterType: 'app' | 'process' | 'crash',
  filterValue: string,
  timeRange?: string,
  _auth?: AuthState | null
): Promise<PerformanceDeviceList> {
  const endpoint = filterType === 'app' ? 'apps' : filterType === 'process' ? 'processes' : 'crashes';
  const params = new URLSearchParams();
  if (timeRange) params.append('time_range', timeRange);

  const queryString = params.toString();
  return request<PerformanceDeviceList>(`/fleet/${endpoint}/${encodeURIComponent(filterValue)}/devices${queryString ? `?${queryString}` : ''}`);
}

/**
 * Fetch sleep periods for a device to display as chart overlays.
 * Sleep periods explain data gaps as device sleep rather than connectivity issues.
 */
export async function fetchSleepPeriods(
  uuid: string,
  timeRange?: string,
  _auth?: AuthState | null
): Promise<SleepPeriodsResponse> {
  const params = new URLSearchParams();
  if (timeRange) params.append('time_range', timeRange);

  const queryString = params.toString();
  return request<SleepPeriodsResponse>(`/telemetry/${encodeURIComponent(uuid)}/sleep-periods${queryString ? `?${queryString}` : ''}`);
}
